import React, { useCallback, useContext, useEffect, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { BaseCompAction, BaseCompSubAction, BaseInfoCompProps, capitalizeAll, DataPoint, MultiSeriesDataPoint, GenericMessageBox }
    from '@moderepo/mode-app-components';
import { NumericMetricInfo, TimeSeriesAggregation } from '@moderepo/mode-internal-apis';
import { TFunction } from 'react-i18next';
import { ValueDashboardWidgetSettings, SingleValueViewType, ValueThresholdReachingType, ValueThresholdMonitorSeverity, MetricMetadata,
    ValueThresholdReachedInfo } from './dashboardTypes';
import { useRitzTranslation } from '../../utils/customHooks';
import { TSDashboardWidgetRequiredViewData } from './dashboardLogic';
import { ValueDashboardWidgetDataViewComp, ValueDashboardWidgetGaugeDataViewComp } from '../../components/dashboard';
import { getDateTimeDisplay } from '../containerUtils';
import { useWidgetDataSource } from './dashboardUtils';
import { DEFAULT_DECIMAL_PLACES } from '../../utils/constants';



const useStyle = makeStyles((theme: Theme) => {
    return {
        widget: {
            width : '100%',
            height: '100%',
        },

        widgetViewContent: {
            width  : '100%',
            height : '100%',
            padding: theme.spacing(2),
        },
    };
}, { name: 'ValueDashboardWidgetDataViewContainer', index: 2 });


/**
 * Check if the value reached the threshold and return a ValueThresholdReachedInfo. If value is good, return undefined
 * @param metadataSetting
 * @param value
 */
const checkThresholds = (metricMetadata: MetricMetadata | undefined, value: number | null | undefined): ValueThresholdReachedInfo | undefined => {
    if (metricMetadata && value !== null && value !== undefined) {

        // NOTE: Not all thresholds are set so we only need to compare to the thresholds that have some defined value

        // IMPORTANT NOTE: We need to check if the value passed the CRITICAL threshold level BEFORE checking if it passed below WARNING
        // level because if a value passed the CRITICAL threshold, it should also passed the WARNING threshold. If we check the
        // warning threshold first, we will treat it as passing WARNING threshold and only show WARNING alert when we should have
        // shown CRITICAL alert
        if (metricMetadata.lowValueCriticalThreshold !== undefined && value <= metricMetadata.lowValueCriticalThreshold) {
            return {
                type    : ValueThresholdReachingType.TOO_LOW,
                severity: ValueThresholdMonitorSeverity.CRITICAL,
            };
        }
        if (metricMetadata.lowValueWarningThreshold !== undefined && value <= metricMetadata.lowValueWarningThreshold) {
            return {
                type    : ValueThresholdReachingType.TOO_LOW,
                severity: ValueThresholdMonitorSeverity.WARNING,
            };
        }

        // Similar to the check for LOW threshold. We need to check for HIGH CRITICAL threshold before checking for HIGH WARNING threshold
        if (metricMetadata.highValueCriticalThreshold !== undefined && value >= metricMetadata.highValueCriticalThreshold) {
            return {
                type    : ValueThresholdReachingType.TOO_HIGH,
                severity: ValueThresholdMonitorSeverity.CRITICAL,
            };
        }
        if (metricMetadata.highValueWarningThreshold !== undefined && value >= metricMetadata.highValueWarningThreshold) {
            return {
                type    : ValueThresholdReachingType.TOO_HIGH,
                severity: ValueThresholdMonitorSeverity.WARNING,
            };
        }
    }

    return undefined;
};


/**
 * Define the interface for the Aggregated Data
 */
export interface AggregatedMetricData {
    readonly numericMetrics?: readonly NumericMetricInfo[] | undefined;
    readonly numericMetricData?: {                                          // A map of data points, 1 per metric. The keys in this map
        readonly [metricName: string]: DataPoint<number> | undefined;       // will be the metric.valueName
    } | undefined;
}



/**
 * Convert a map of data points' numeric value for each metric to a map of value STRINGs.
 */
const localizeAggregatedData = (
    source: AggregatedMetricData['numericMetricData'] | undefined,
    metricMetadata: MetricMetadata| undefined,
    trans: TFunction,
): {
    readonly [metricName: string]: string
} | undefined => {

    if (!source) {
        return undefined;
    }

    // For each metricName, go through the array of data points and add all the decimal places for that metric name
    return Object.entries(source).reduce(
        (result, [metricName, dataPoint]): {
            readonly [metricName: string]: string
        } => {
            if (dataPoint) {
                const decimalPlaces = metricMetadata?.[metricName].decimalPlaces ?? DEFAULT_DECIMAL_PLACES;

                return {
                    ...result,
                    [metricName]: trans('{{value, number-format}}',
                        {
                            value: dataPoint.value,
                            decimalPlaces,
                        }),
                };
            }
            return result;
        }, {},
    );
};


export interface ValueDashboardWidgetDataViewContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly selectedWidget: ValueDashboardWidgetSettings;

    // This is a container that wraps a DUMB component and some time we need to pass some props such as className, styles, etc... into the
    // component from the container of this container so this is a way for us to pass props into the component
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
    } | undefined;

    readonly widgetPrimaryActions?: readonly BaseCompAction[] | undefined;
    readonly widgetSecondaryActions?: readonly (BaseCompAction | BaseCompSubAction)[] | undefined;
    readonly metricsData?: TSDashboardWidgetRequiredViewData | undefined;
}



/**
 * This is the component for displaying the UI/data view for the ValueDashboardWidget.
 */
export const ValueDashboardWidgetDataViewContainer: React.FC<ValueDashboardWidgetDataViewContainerProps> = (
    props: ValueDashboardWidgetDataViewContainerProps,
) => {

    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();

    // For optimization, use the 'useWidgetDataSource' hook to get the dataSource. This hook will make sure this object
    // doesn't change unless the content of the dataSource changed.
    const dataSource = useWidgetDataSource(props.selectedWidget.dataSource);


    // metricsData contains array of data points. For this widget, we only need 1 data point. So if the user requested aggregated data over
    // a period of time, we will aggregate them all into 1 value. For example: if the user requested "average" data for the last 1 week,
    // we will compute the average of those data.
    const [aggregatedMetricsData, setAggregatedMetricsData] = useState<{
        readonly previous: AggregatedMetricData | undefined;
        readonly current: AggregatedMetricData | undefined
        readonly thresholdMonitorStatus?: {
            [metricName: string]: ValueThresholdReachedInfo | undefined
        } | undefined,
    } | undefined>();


    useEffect(() => {

        if (props.metricsData && props.metricsData.numericMetrics && props.metricsData.numericMetrics.length > 0
            && props.metricsData.numericMetricsData && props.metricsData.numericMetricsData.length > 0) {


            /**
             * Aggregate an array of data points and return a MAP of DataPoints, one for each metric with the metric's valueName as key in the map.
             *
             * For example: Give this Array of multiseries data points and aggregation type is SUM
             *      dataPointArray = [{
             *          date: "2021/10/05",
             *          values: {
             *              temperature: 56,
             *              pressure: 23,
             *              humidity: 34,
             *          }
             *      }, {
             *          date: "2021/10/06",
             *          values: {
             *              temperature: 12,
             *              pressure: 34,
             *              humidity: 25,
             *          }
             *      }, {
             *          date: "2021/10/07",
             *          values: {
             *              temperature: null,
             *              pressure: 32,
             *              humidity: 13,
             *          }
             *      }]
             *
             * This function will return this Object
             *      {
             *          temperature: {
             *              date: "2021/10/07",
             *              value: 68               // 56+12
             *          },
             *          pressure: {
             *              date: "2021/10/07",
             *              value: 89               // 23+34+32
             *          },
             *          humidity: {
             *              date: "2021/10/07",
             *              value: 72,              // 34+25+13
             *          },
             *      }
             *
             * If Aggregation type is AVERAGE, this function will return this
             *      {
             *          temperature: {
             *              date: "2021/10/07",
             *              value: 34               // (56+12)/2
             *          },
             *          pressure: {
             *              date: "2021/10/07",
             *              value: 29.666           // (23+34+32)/3
             *          },
             *          humidity: {
             *              date: "2021/10/07",
             *              value: 24,              // (34+25+13)/3
             *          },
             *      }
             */
            const getAggregatedData = (
                metrics: readonly NumericMetricInfo[],
                dataPointArray: readonly MultiSeriesDataPoint<number>[],
            ): {
                readonly [measurementNames: string]: DataPoint<number> | undefined;
            } => {

                // Get the name of all the metrics that was selected for this widget
                const metricNames = metrics.map((m) => {
                    return m.name;
                });


                switch (dataSource.aggregationType) {
                    case TimeSeriesAggregation.SUM:
                    case TimeSeriesAggregation.COUNT:

                        // For each metricName, go through the array of data points and add all the values for that metric name
                        const sumAggregatedData = metricNames.reduce((
                            result: {
                                readonly [metricName: string]: DataPoint<number> | undefined
                            },
                            metricName,
                        ) => {
                            if (dataPointArray.length > 0) {

                                // Calculate the sum for 1 metric
                                const singleMetricSum = dataPointArray.reduce((
                                    sum: number,
                                    dataPoint: MultiSeriesDataPoint<number>,
                                ) => {
                                    return sum + (dataPoint.values[metricName] ?? 0);
                                }, 0);

                                // Create a DataPoint using the singleMetricSum as the data point's value. As for the data point's date,
                                // copy the date of last data point from the dataPointArray
                                const dataPoint = {
                                    dateValue : dataPointArray[dataPointArray.length - 1].dateValue,
                                    dateString: getDateTimeDisplay(
                                        (new Date(dataPointArray[dataPointArray.length - 1].dateValue)).toISOString(), trans, false,
                                    ),
                                    value: singleMetricSum,
                                };

                                // Add the dataPoint to the result aggregated data
                                return {
                                    ...result,
                                    [metricName]: dataPoint,
                                };
                            }
                            return result;
                        }, {});

                        return sumAggregatedData;

                    case TimeSeriesAggregation.MAX:

                        // For each metricName, go through the array of data points and find the largest data point for that metric name
                        const maxAggregatedData = metricNames.reduce((
                            result: {
                                readonly [metricName: string]: DataPoint<number> | undefined
                            },
                            metricName,
                        ) => {
                            if (dataPointArray.length > 0) {

                                // Go through the dataPointArray and find the data point that has the largest value for the given 'metricName'
                                const maxDataPoint = dataPointArray.reduce((
                                    currentMaxDataPoint: DataPoint<number> | undefined,
                                    dataPoint: MultiSeriesDataPoint<number>,
                                ) => {
                                    if (currentMaxDataPoint) {
                                        // If we already have a currentMaxDataPoint then need to compare the currentMaxDataPoint with the
                                        // new dataPoint to see which one is larger and return the larger one.
                                        const newValue = dataPoint.values[metricName];
                                        if (typeof currentMaxDataPoint.value === 'number' && typeof newValue === 'number'
                                                && newValue >= currentMaxDataPoint.value) {

                                            // The new 'dataPoint' is larger so return this data point.
                                            return {
                                                dateValue : dataPoint.dateValue,
                                                dateString: getDateTimeDisplay((new Date(dataPoint.dateValue)).toISOString(), trans, false),
                                                value     : dataPoint.values[metricName],
                                            };
                                        }

                                        // If we get here that mean the new dataPoint is not larger than then currentMaxDataPoint
                                        return currentMaxDataPoint;
                                    }

                                    // If we have not found the max data point yet, then the current data point should be the max data point
                                    // if it has value. If it has null or undefined as value, don't use it
                                    if (dataPoint.values[metricName] !== null && dataPoint.values[metricName] !== undefined) {
                                        return {
                                            dateValue : dataPoint.dateValue,
                                            dateString: getDateTimeDisplay((new Date(dataPoint.dateValue)).toISOString(), trans, false),
                                            value     : dataPoint.values[metricName],
                                        };
                                    }

                                    return undefined;
                                }, undefined);


                                // Add the maxDataPoint to the result aggregated data
                                return {
                                    ...result,
                                    [metricName]: maxDataPoint,
                                };
                            }

                            return result;
                        }, {});

                        return maxAggregatedData;

                    case TimeSeriesAggregation.MIN:

                        // For each metricName, go through the array of data points and find the smallest data point for that metric name
                        const minAggregatedData = metricNames.reduce((
                            result: {
                                readonly [metricName: string]: DataPoint<number> | undefined
                            },
                            metricName,
                        ) => {
                            if (dataPointArray.length > 0) {

                                // Go through the dataPointArray and find the data point that has the smallest value for the given 'metricName'
                                const minDataPoint = dataPointArray.reduce((
                                    currentMinDataPoint: DataPoint<number> | undefined,
                                    dataPoint: MultiSeriesDataPoint<number>,
                                ) => {
                                    if (currentMinDataPoint) {
                                        // If we already have a currentMinDataPoint then need to compare the currentMinDataPoint with the
                                        // new dataPoint to see which one is larger and return the larger one.
                                        const newValue = dataPoint.values[metricName];
                                        if (typeof currentMinDataPoint.value === 'number' && typeof newValue === 'number'
                                                    && newValue <= currentMinDataPoint.value) {

                                            // The new 'dataPoint' is larger so return this data point.
                                            return {
                                                dateValue : dataPoint.dateValue,
                                                dateString: getDateTimeDisplay((new Date(dataPoint.dateValue)).toISOString(), trans, false),
                                                value     : dataPoint.values[metricName],
                                            };
                                        }

                                        // If we get here that mean the new dataPoint is not smaller than then currentMinDataPoint
                                        return currentMinDataPoint;
                                    }

                                    // If we have not found the min data point yet, then the current data point should be the min data point
                                    // if it has value. If it has null or undefined as value, don't use it
                                    if (dataPoint.values[metricName] !== null && dataPoint.values[metricName] !== undefined) {
                                        return {
                                            dateValue : dataPoint.dateValue,
                                            dateString: getDateTimeDisplay((new Date(dataPoint.dateValue)).toISOString(), trans, false),
                                            value     : dataPoint.values[metricName],
                                        };
                                    }

                                    return undefined;
                                }, undefined);


                                // Add the minDataPoint to the result aggregated data
                                return {
                                    ...result,
                                    [metricName]: minDataPoint,
                                };
                            }

                            return result;
                        }, {});

                        return minAggregatedData;

                    default:
                        // Assume the aggregationType is AVG. Add up all the data point values and then divide them by the number of data points

                        const avgAggregatedData = metricNames.reduce((
                            result: {
                                readonly [metricName: string]: DataPoint<number> | undefined
                            },
                            metricName,
                        ) => {
                            if (dataPointArray.length > 0) {

                                // Calculate the sum for 1 metric. While we sum up the data points' values, we also need to keep track of the
                                // count of the number of data points that are included in the sum so that when we calculate the average,
                                // we don't include the null/undefined values.
                                const { sum, count } = dataPointArray.reduce((
                                    currentResult: {
                                        sum: number, count: number
                                    },
                                    dataPoint: MultiSeriesDataPoint<number>,
                                ) => {
                                    // Only include the data points that have value not null/undefined.
                                    if (typeof dataPoint.values[metricName] === 'number') {
                                        return {
                                            sum  : currentResult.sum + (dataPoint.values[metricName] as number),
                                            count: currentResult.count + 1,
                                        };
                                    }
                                    return currentResult;
                                }, { sum: 0, count: 0 });


                                if (count > 0) {
                                    const avg = sum / count;

                                    // Create a DataPoint using the 'avg' as the data point's value. As for the data point's date,
                                    // copy the date of last data point from the dataPointArray
                                    const dataPoint = {
                                        dateValue : dataPointArray[dataPointArray.length - 1].dateValue,
                                        dateString: getDateTimeDisplay(
                                            (new Date(dataPointArray[dataPointArray.length - 1].dateValue)).toISOString(), trans, false,
                                        ),
                                        value: avg,
                                    };

                                    // Add the dataPoint to the result aggregated data
                                    return {
                                        ...result,
                                        [metricName]: dataPoint,
                                    };
                                }
                            }
                            return result;
                        }, {});

                        return avgAggregatedData;
                }
            };

            const aggregatedData = getAggregatedData(props.metricsData.numericMetrics, props.metricsData.numericMetricsData);

            // Check the aggregated data and see if the value for any of the metric reached the threshold.
            // We only need to do this if the widget.viewSettings.monitorValueThresholds is true
            const allMetricsThresholdInfo = props.selectedWidget.viewSettings.monitorValueThresholds
                ? Object.keys(aggregatedData).reduce((
                    result, metricName,
                ) => {
                    // Call checkThresholds to check for threshold and if there is a threshold reached, expect a ValueThresholdInfo object back.
                    const thresholdReachedInfo = checkThresholds(
                        dataSource.metricsMetadata[metricName],
                        aggregatedData[metricName]?.value,
                    );
                    if (thresholdReachedInfo) {
                        return {
                            ...result,
                            [metricName]: thresholdReachedInfo,
                        };
                    }
                    return result;
                }, {})
                : undefined;



            // return the similar data as metricsData except replace the array of data points with the SINGLE aggregated data point wrapped in
            // an array. We need to keep the structure the same.
            setAggregatedMetricsData((previousData) => {
                return {
                    previous: previousData?.current,       // Set the current aggregatedMetricsData as the previous data
                    current : {
                        numericMetrics   : props.metricsData?.numericMetrics,
                        numericMetricData: aggregatedData,
                    },
                    thresholdMonitorStatus: allMetricsThresholdInfo,
                };
            });

        } else if (props.metricsData) {

            // If there is metricsData object but it gets here then it means there is no data
            setAggregatedMetricsData((previousData) => {
                return {
                    previous: previousData?.current,       // Set the current aggregatedMetricsData as the previous data
                    current : {
                        numericMetrics   : props.metricsData?.numericMetrics,
                        numericMetricData: {},
                    },
                };
            });
        }
    }, [props.metricsData, dataSource, props.selectedWidget.viewSettings.monitorValueThresholds, trans]);



    /**
     * Create view component that display data as raw value
     */
    const createRawValueViewComponent = useCallback((
        previous: AggregatedMetricData | undefined,
        current: AggregatedMetricData,
        thresholdMonitorStatus: {
            [metricName: string]: ValueThresholdReachedInfo | undefined
        } | undefined,
    ): React.ReactNode => {
        const localizedAggregatedData = localizeAggregatedData(current.numericMetricData, dataSource?.metricsMetadata, trans);

        return (
            <ValueDashboardWidgetDataViewComp
                className={clsx(classes.widget, props.className, props.infoCompProps?.className, classes.widget)}
                title={props.selectedWidget.title}
                icon={props.selectedWidget.icon}
                customPrimaryActions={props.widgetPrimaryActions}
                customSecondaryActions={props.widgetSecondaryActions}
                layout={props.selectedWidget.viewSettings.viewLayout}
                valueDisplaySize={props.selectedWidget.viewSettings.valueDisplaySize}
                valueDisplayColor={props.selectedWidget.viewSettings.valueDisplayColor}
                showLabel={props.selectedWidget.viewSettings.showLabel}
                aggregatedData={current}
                localizedAggregatedData={localizedAggregatedData}
                previousAggregatedData={previous}
                thresholdMonitorStatus={props.selectedWidget.viewSettings.monitorValueThresholds ? thresholdMonitorStatus : undefined}
                noDataMessageComp={(
                    <GenericMessageBox
                        title={trans('generic.no_data.title', {
                            term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                        })}
                    />
                )}
                showCustomActionOnHover
            />
        );
    }, [trans, dataSource, classes.widget, props.className,
        props.infoCompProps?.className, props.selectedWidget.title, props.selectedWidget.icon,
        props.selectedWidget.viewSettings.viewLayout, props.selectedWidget.viewSettings.valueDisplaySize,
        props.selectedWidget.viewSettings.valueDisplayColor, props.selectedWidget.viewSettings.showLabel,
        props.selectedWidget.viewSettings.monitorValueThresholds, props.widgetPrimaryActions, props.widgetSecondaryActions]);



    /**
     * Create view component that use Gauge to display data
     */
    const createGaugeViewComponent = useCallback((
        current: AggregatedMetricData,
        thresholdMonitorStatus: {
            [metricName: string]: ValueThresholdReachedInfo | undefined
        } | undefined,
    ): React.ReactNode => {
        const localizedAggregatedData = localizeAggregatedData(current.numericMetricData, dataSource?.metricsMetadata, trans);

        return (
            <ValueDashboardWidgetGaugeDataViewComp
                className={clsx(classes.widget, props.className, props.infoCompProps?.className, classes.widget)}
                title={props.selectedWidget.title}
                icon={props.selectedWidget.icon}
                customPrimaryActions={props.widgetPrimaryActions}
                customSecondaryActions={props.widgetSecondaryActions}
                layout={props.selectedWidget.viewSettings.viewLayout}
                showLabel={props.selectedWidget.viewSettings.showLabel}
                valueDisplayColor={props.selectedWidget.viewSettings.valueDisplayColor}
                widgetId={props.selectedWidget.id}
                aggregatedData={current}
                localizedAggregatedData={localizedAggregatedData}
                monitorValueThresholds={props.selectedWidget.viewSettings.monitorValueThresholds}
                thresholdMonitorStatus={props.selectedWidget.viewSettings.monitorValueThresholds ? thresholdMonitorStatus : undefined}
                thresholdMonitorSettings={props.selectedWidget.viewSettings.monitorValueThresholds ? dataSource.metricsMetadata : undefined}
                yAxisRangeSettings={props.selectedWidget.viewSettings.metricsMetadata}
                noDataMessageComp={(
                    <GenericMessageBox
                        title={trans('error.generic_empty_list.title', {
                            term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                        })}
                    />
                )}
                showCustomActionOnHover
            />
        );
    }, [classes.widget, dataSource.metricsMetadata, props.className, props.infoCompProps?.className, props.selectedWidget.icon,
        props.selectedWidget.id, props.selectedWidget.title, props.selectedWidget.viewSettings.metricsMetadata,
        props.selectedWidget.viewSettings.monitorValueThresholds, props.selectedWidget.viewSettings.showLabel,
        props.selectedWidget.viewSettings.valueDisplayColor, props.selectedWidget.viewSettings.viewLayout, props.widgetPrimaryActions,
        props.widgetSecondaryActions, trans]);



    /**
     * Create the view component e.g. LineChart, BarChart, GanttChart, Table, etc... based on the widget settings. This function will not
     * create a view, it will call the appropriate helper function to create specific view based on the widget settings.
     */
    const createViewComponent = useCallback((
        previous: AggregatedMetricData | undefined,
        current: AggregatedMetricData,
        thresholdMonitorStatus: {
            [metricName: string]: ValueThresholdReachedInfo | undefined
        } | undefined,
    ): React.ReactNode => {
        switch (props.selectedWidget.viewSettings.viewType) {
            case SingleValueViewType.VALUE:
                return createRawValueViewComponent(previous, current, thresholdMonitorStatus);
            case SingleValueViewType.GAUGE:
                return createGaugeViewComponent(current, thresholdMonitorStatus);
            default:
                return (
                    <div>Unsupported View Type {`${props.selectedWidget.viewSettings.viewType}`}</div>
                );
        }
    }, [createGaugeViewComponent, createRawValueViewComponent, props.selectedWidget.viewSettings.viewType]);


    return (
        <>
            {aggregatedMetricsData?.current?.numericMetricData
                && createViewComponent(
                    aggregatedMetricsData.previous,
                    aggregatedMetricsData.current,
                    aggregatedMetricsData.thresholdMonitorStatus,
                )}
        </>
    );
};
